/**
 * Weak-password detection for the sign-up form.
 *
 * Scope note: because PASSWORD_MIN_LENGTH is 12, the classic leaked-password list
 * ("password", "123456", "qwerty") is almost entirely irrelevant — those fail on
 * length before they ever reach here. What actually gets through a 12-character
 * floor is *padding*: a common word with digits stapled on, a keyboard walk, a
 * repeated character, or a long digit run. So this module is deliberately
 * pattern-first with a small curated list, rather than a large word list that
 * would mostly consist of strings the length check already rejects.
 *
 * This is a client-side check. Standard Firebase Auth cannot enforce a password
 * policy server-side (that is an Identity Platform feature we deliberately did
 * not buy), so this protects users from weak choices — it does not stop an
 * attacker calling identitytoolkit directly. Bundled and offline on purpose: a
 * breach-corpus lookup would mean a network call from an MV3 page to a third
 * party, a new host permission, a CSP entry, and a store-review conversation.
 */

/** Long passwords that are themselves common. Lowercased. */
export const COMMON_PASSWORDS = new Set([
    'password1234', 'password123456', 'passwordpassword', 'password12345',
    'qwertyuiop123', 'qwertyuiopasdf', 'qwertyuiop1234', 'qwerty123456',
    'iloveyou1234', 'iloveyou123456', 'iloveyouforever', 'ilovemyfamily',
    '123456789012', '1234567890123', '12345678901234', '123456789101112',
    '111111111111', '000000000000', '123123123123', '121212121212',
    'abcdefghijkl', 'abcd1234abcd', 'abcdefg123456', 'aaaaaaaaaaaa',
    'letmein12345', 'letmeinplease', 'welcome123456', 'welcometoyou',
    'trustno1trustno1', 'superman1234', 'batman123456', 'pokemon12345',
    'football1234', 'baseball1234', 'basketball12', 'liverpool123',
    'princess1234', 'sunshine1234', 'butterfly123', 'chocolate123',
    'michael12345', 'jennifer1234', 'jordan123456', 'thomas123456',
    'monkey123456', 'dragon123456', 'master123456', 'shadow123456',
    'administrator', 'administrator1', 'qazwsxedcrfv', 'zaq12wsxcde3',
    '1qaz2wsx3edc', '1q2w3e4r5t6y', 'q1w2e3r4t5y6', 'asdfghjkl123',
    'passw0rd1234', 'p@ssw0rd1234', 'passwordabc1', 'mypassword123',
    'changeme1234', 'temporary123', 'secretpassword', 'thisisapassword',
    'allahuakbar1', 'bismillah123', 'alhamdulillah',
]);

/**
 * Walks are matched as substrings of these. The concatenated entries matter as
 * much as the single rows: people routinely walk ACROSS rows
 * ("qwertyuiopasdfghjkl"), which a per-row check misses entirely.
 */
const KEYBOARD_ROWS = [
    'qwertyuiopasdfghjklzxcvbnm',
    'azertyuiopqsdfghjklmwxcvbn',
    'qwertzuiopasdfghjklyxcvbnm',
    '1234567890',
    '1qaz2wsx3edc4rfv5tgb6yhn',
    'qazwsxedcrfvtgbyhnujmikol',
];

/** Strip leading/trailing padding people add to hit a length rule. */
const stripPadding = (s: string): string =>
    s.replace(/^[!@#$%^&*_.\-\d]+/, '').replace(/[!@#$%^&*_.\-\d]+$/, '');

/** "aaaaaaaaaaaa", "abababababab" — one short unit repeated to length. */
const isRepeatedUnit = (s: string): boolean => {
    for (let unit = 1; unit <= 4; unit++) {
        if (s.length % unit !== 0) continue;
        const head = s.slice(0, unit);
        if (head.repeat(s.length / unit) === s) return true;
    }
    return false;
};

/** "abcdefghijkl", "9876543210" — a long ascending or descending run. */
const isSequentialRun = (s: string): boolean => {
    if (s.length < 8) return false;
    let ascending = true;
    let descending = true;
    for (let i = 1; i < s.length; i++) {
        const step = s.charCodeAt(i) - s.charCodeAt(i - 1);
        if (step !== 1) ascending = false;
        if (step !== -1) descending = false;
        if (!ascending && !descending) return false;
    }
    return true;
};

/** A straight walk along one keyboard row, forwards or backwards. */
const isKeyboardWalk = (s: string): boolean => {
    if (s.length < 8) return false;
    const reversed = s.split('').reverse().join('');
    return KEYBOARD_ROWS.some((row) => row.includes(s) || row.includes(reversed));
};

/**
 * Short passwords that are only ever seen padded up to length. Kept separate
 * from COMMON_PASSWORDS because these are matched against the *stripped* core.
 */
const COMMON_BASES = new Set([
    'password', 'passwort', 'contrasena', 'motdepasse', 'welcome', 'letmein',
    'qwerty', 'qwertyuiop', 'asdfgh', 'asdfghjkl', 'zxcvbn', 'zxcvbnm',
    'iloveyou', 'princess', 'sunshine', 'butterfly', 'chocolate', 'flower',
    'monkey', 'dragon', 'master', 'shadow', 'ninja', 'mustang', 'superman',
    'batman', 'pokemon', 'football', 'baseball', 'basketball', 'soccer',
    'liverpool', 'arsenal', 'chelsea', 'barcelona', 'realmadrid',
    'michael', 'jennifer', 'jessica', 'jordan', 'thomas', 'daniel', 'ashley',
    'admin', 'administrator', 'root', 'guest', 'test', 'demo', 'temp',
    'changeme', 'secret', 'trustno', 'whatever', 'freedom', 'starwars',
    'computer', 'internet', 'samsung', 'google', 'facebook', 'android',
    'rhemn', 'muslim', 'islam', 'quran', 'bismillah', 'alhamdulillah',
]);

/**
 * True when the password is weak despite clearing the length floor.
 * Input is lowercased by the caller.
 */
export function isWeakPattern(lowered: string): boolean {
    if (COMMON_PASSWORDS.has(lowered)) return true;
    if (isRepeatedUnit(lowered)) return true;
    if (isSequentialRun(lowered)) return true;
    if (isKeyboardWalk(lowered)) return true;

    // "password!!!!", "sunshine2024" — a common base with padding stapled on.
    const core = stripPadding(lowered);
    if (core.length >= 4 && core !== lowered) {
        if (COMMON_PASSWORDS.has(core)) return true;
        if (isKeyboardWalk(core)) return true;
        if (COMMON_BASES.has(core)) return true;
    }

    return COMMON_BASES.has(lowered);
}
